// material-ui
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import { Notification } from './data';
import Avatar from 'components/@extended/Avatar';
import Dot from 'components/@extended/Dot';

import { getImageUrl, ImagePath } from 'utils/getImageUrl';
import { Link as RouterLink } from 'react-router-dom';

// ==============================|| NOTIFICATION - ITEMS ||============================== //

export default function NotificationItem({ notification, onClick }: { notification: Notification; onClick?: () => void }) {
  const { user, message, time, context, read, file, actions, type, requestLink } = notification;
  const avatarSrc = user?.avatar
    ? user.avatar.startsWith('http') || user.avatar.startsWith('/uploads')
      ? user.avatar
      : getImageUrl(`${user.avatar}`, ImagePath.USERS)
    : undefined;

  // Renders the file attachment details for 'file_upload' type
  const renderFileAttachment = () => {
    if (type !== 'file_upload' || !file) return null;
    return (
      <Stack direction="row" sx={{ gap: 0.75, alignItems: 'center' }}>
        <Avatar
          alt={file.name}
          src={file.icon}
          size="xs"
          color="secondary"
          variant="rounded"
          type="combined"
          sx={{ p: 0.125, width: 24, height: 24 }}
        />
        <Typography variant="body2">{file.name}</Typography>
        <Typography variant="body2" color="secondary">
          {file.size}
        </Typography>
      </Stack>
    );
  };

  // Renders action buttons or alerts
  const renderActions = () => {
    if (!actions && !requestLink) return null;

    return (
      <Stack component="span" direction="row" spacing={1}>
        {requestLink && (
          <Box>
            <Button
              component={RouterLink}
              to={requestLink}
              size="small"
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              View Request
            </Button>
          </Box>
        )}
        {(actions || []).map((action: any, index) => (
          <Box key={index}>
            {action.type === 'button' && <Button component="span" {...action?.buttonProps} />}
            {action.type === 'alert' && <Alert component="span" {...action?.alertProps} />}
          </Box>
        ))}
      </Stack>
    );
  };

  return (
    <ListItemButton key={notification.id} sx={{ p: { xs: 1.25, sm: 2.25 }, alignItems: 'flex-start' }} divider onClick={onClick}>
      <ListItemAvatar>
        <Avatar alt={user?.name} src={avatarSrc} />
      </ListItemAvatar>
      <ListItemText
        disableTypography
        primary={
          <Typography
            variant="subtitle1"
            sx={{
              display: 'flex',
              gap: 0.25,
              flexDirection: 'row',
              alignItems: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: { xs: 180, sm: 320 }
            }}
          >
            {user?.name || 'System'}
            <Typography variant="body1">{notification.title || message}</Typography>
            {type === 'mention' && 'Mantis.'}
          </Typography>
        }
        secondary={
          <Stack component="span" sx={{ gap: 1.5 }}>
            <Typography
              component="span"
              variant="caption"
              sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', color: 'text.secondary' }}
            >
              {time}
              {context && <Dot component="span" size={3} color="secondary" />}
              {context}
            </Typography>
            {renderFileAttachment()}
            {renderActions()}
          </Stack>
        }
        sx={{ my: 0 }}
      />
      {!read && <Dot size={6} style={{ marginTop: 20 }} />}
    </ListItemButton>
  );
}
